import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import MovieIcon from '@mui/icons-material/Movie'
import TvIcon from '@mui/icons-material/Tv'
import GridViewIcon from '@mui/icons-material/GridView'
import ViewListIcon from '@mui/icons-material/ViewList'
import PeopleIcon from '@mui/icons-material/People'
import { MoviePoster, RankBadge } from '@aperture/ui'
import { useUserRatings } from '../../hooks/useUserRatings'
import { useWatching } from '../../hooks/useWatching'
import { useViewMode } from '../../hooks/useViewMode'
import { TopPicksMovieListItem, TopPicksSeriesListItem } from './components'

interface PopularMovie {
  movieId: string
  title: string
  year: number | null
  posterUrl: string | null
  backdropUrl: string | null
  overview: string | null
  genres: string[]
  communityRating: number | null
  uniqueViewers: number
  playCount: number
  popularityScore: number
  rank: number
}

interface PopularSeries {
  seriesId: string
  title: string
  year: number | null
  posterUrl: string | null
  backdropUrl: string | null
  overview: string | null
  genres: string[]
  communityRating: number | null
  network: string | null
  uniqueViewers: number
  totalEpisodesWatched: number
  avgCompletionRate: number
  popularityScore: number
  rank: number
}

interface TopPicksConfig {
  moviesTimeWindowDays: number
  seriesTimeWindowDays: number
  moviesCount: number
  seriesCount: number
  moviesMinUniqueViewers: number
  seriesMinUniqueViewers: number
  lastRefreshedAt: string | null
}

export function TopPicksPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getRating, setRating } = useUserRatings()
  const { isWatching, toggleWatching } = useWatching()
  
  const initialTab = searchParams.get('tab') === 'series' ? 1 : 0
  const [tabIndex, setTabIndex] = useState(initialTab)
  const [movies, setMovies] = useState<PopularMovie[]>([])
  const [series, setSeries] = useState<PopularSeries[]>([])
  const [moviesConfig, setMoviesConfig] = useState<TopPicksConfig | null>(null)
  const [seriesConfig, setSeriesConfig] = useState<TopPicksConfig | null>(null)
  const [moviesLoading, setMoviesLoading] = useState(true)
  const [seriesLoading, setSeriesLoading] = useState(true)
  const [moviesError, setMoviesError] = useState<string | null>(null)
  const [seriesError, setSeriesError] = useState<string | null>(null)
  const { viewMode, setViewMode } = useViewMode('topPicks')

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
    setSearchParams({ tab: newValue === 1 ? 'series' : 'movies' })
  }

  const handleRateMovie = useCallback(
    async (movieId: string, rating: number | null) => {
      try {
        await setRating('movie', movieId, rating)
      } catch (err) {
        console.error('Failed to rate movie:', err)
      }
    },
    [setRating]
  )

  const handleRateSeries = useCallback(
    async (seriesId: string, rating: number | null) => {
      try {
        await setRating('series', seriesId, rating)
      } catch (err) {
        console.error('Failed to rate series:', err)
      }
    },
    [setRating]
  )

  useEffect(() => {
    const fetchTopMovies = async () => {
      try {
        const response = await fetch('/api/top-picks/movies', { credentials: 'include' })
        if (response.ok) {
          const data = await response.json()
          setMovies(data.movies)
          setMoviesConfig(data.config)
          setMoviesError(null)
        } else {
          setMoviesError(t('topPicksPage.errorLoadMovies'))
        }
      } catch {
        setMoviesError(t('topPicksPage.errorConnect'))
      } finally {
        setMoviesLoading(false)
      }
    }

    const fetchTopSeries = async () => {
      try {
        const response = await fetch('/api/top-picks/series', { credentials: 'include' })
        if (response.ok) {
          const data = await response.json()
          setSeries(data.series)
          setSeriesConfig(data.config)
          setSeriesError(null)
        } else {
          setSeriesError(t('topPicksPage.errorLoadSeries'))
        }
      } catch {
        setSeriesError(t('topPicksPage.errorConnect'))
      } finally {
        setSeriesLoading(false)
      }
    }

    fetchTopMovies()
    fetchTopSeries()
  }, [t])

  const config = tabIndex === 0 ? moviesConfig : seriesConfig
  const loading = tabIndex === 0 ? moviesLoading : seriesLoading
  const error = tabIndex === 0 ? moviesError : seriesError

  const renderLoadingSkeleton = () => (
    <Grid container spacing={2}>
      {[...Array(10)].map((_, i) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
          <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '2/3', borderRadius: 1 }} />
        </Grid>
      ))}
    </Grid>
  )

  const renderMoviesGrid = () => (
    <Grid container spacing={2}>
      {movies.map((movie) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={movie.movieId}>
          <Box position="relative">
            <MoviePoster
              title={movie.title}
              year={movie.year}
              posterUrl={movie.posterUrl}
              genres={movie.genres}
              rating={movie.communityRating}
              overview={movie.overview}
              userRating={getRating('movie', movie.movieId)}
              onRate={(rating) => handleRateMovie(movie.movieId, rating)}
              responsive
              onClick={() => navigate(`/movies/${movie.movieId}`)}
            />
            <RankBadge rank={movie.rank} size="large" />
            <Chip
              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
              label={movie.uniqueViewers}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.75)',
                color: 'white',
                fontSize: '0.7rem',
                height: 24,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  )

  const renderMoviesList = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {movies.map((movie) => (
        <TopPicksMovieListItem
          key={movie.movieId}
          movie={movie}
          userRating={getRating('movie', movie.movieId)}
          onRate={(rating) => handleRateMovie(movie.movieId, rating)}
        />
      ))}
    </Box>
  )

  const renderSeriesGrid = () => (
    <Grid container spacing={2}>
      {series.map((show) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={show.seriesId}>
          <Box position="relative">
            <MoviePoster
              title={show.title}
              year={show.year}
              posterUrl={show.posterUrl}
              genres={show.genres}
              rating={show.communityRating}
              overview={show.overview}
              userRating={getRating('series', show.seriesId)}
              onRate={(rating) => handleRateSeries(show.seriesId, rating)}
              responsive
              isWatching={isWatching(show.seriesId)}
              onWatchingToggle={() => toggleWatching(show.seriesId)}
              size="medium"
              onClick={() => navigate(`/series/${show.seriesId}`)}
            />
            <RankBadge rank={show.rank} size="large" />
            <Chip
              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
              label={show.uniqueViewers}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.75)',
                color: 'white',
                fontSize: '0.7rem',
                height: 24,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
            {show.network && (
              <Chip
                label={show.network}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(139, 92, 246, 0.9)',
                  color: 'white',
                  fontSize: '0.65rem',
                  height: 20,
                  maxWidth: 80,
                  '& .MuiChip-label': { 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  )

  const renderSeriesList = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {series.map((show) => (
        <TopPicksSeriesListItem
          key={show.seriesId}
          series={show}
          userRating={getRating('series', show.seriesId)}
          onRate={(rating) => handleRateSeries(show.seriesId, rating)}
        />
      ))}
    </Box>
  )

  const renderContent = () => {
    if (loading) return renderLoadingSkeleton()

    if (error) {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )
    }

    if (tabIndex === 0) {
      if (movies.length === 0) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('topPicksPage.emptyMovies')}
          </Alert>
        )
      }
      return viewMode === 'grid' ? renderMoviesGrid() : renderMoviesList()
    } else {
      if (series.length === 0) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('topPicksPage.emptySeries')}
          </Alert>
        )
      }
      return viewMode === 'grid' ? renderSeriesGrid() : renderSeriesList()
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={{ xs: 0, sm: 1 }}>
            <WhatshotIcon sx={{ color: '#f97316', fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700}>
              {t('topPicksPage.pageTitle')}
            </Typography>
          </Box>
          {!isMobile && (
            <>
              <Typography variant="body1" color="text.secondary">
                {config
                  ? t('topPicksPage.rankedSubtitle', {
                      days: tabIndex === 0 ? config.moviesTimeWindowDays : config.seriesTimeWindowDays,
                    })
                  : t('topPicksPage.rankedSubtitleNoWindow')}
              </Typography>
              {config?.lastRefreshedAt && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {t('topPicksPage.lastRefreshed', {
                    date: new Date(config.lastRefreshedAt).toLocaleDateString(),
                  })}
                </Typography>
              )}
            </>
          )}
        </Box>
        {/* Grid/List toggle always in upper right */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="grid">
            <GridViewIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabIndex} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab 
            icon={<MovieIcon />} 
            iconPosition="start" 
            label={t('topPicksPage.tabMovies')} 
            sx={{ 
              color: tabIndex === 0 ? '#6366f1' : 'text.secondary',
              '&.Mui-selected': { color: '#6366f1' },
            }}
          />
          <Tab 
            icon={<TvIcon />} 
            iconPosition="start" 
            label={t('topPicksPage.tabSeries')} 
            sx={{ 
              color: tabIndex === 1 ? '#ec4899' : 'text.secondary',
              '&.Mui-selected': { color: '#ec4899' },
            }}
          />
        </Tabs>
      </Box>

      {renderContent()}
    </Box>
  )
}
